import React, { useMemo, useState } from "react";
import { Alert, Box, CircularProgress, IconButton, ListSubheader, Menu, MenuItem, Select, SelectChangeEvent, Slider, Stack, Typography, useTheme } from "@mui/material";
import { ResponsiveContainer, ComposedChart, CartesianGrid, XAxis, YAxis, Tooltip, Area, Line } from "recharts";
import { useForecast } from "../../hooks/useForecast";
import { WeeklyForecastComponentType } from "@domain/forecast/enums/WeeklyForecastComponentType.enum";
import { APP_CONFIG } from "../../../../config/app.config";
import { SeasonalityMode, TrendMode } from "@domain/forecast/models/ForecastParameters";
import { MoreVert } from "@mui/icons-material";
import { useProduct } from "@features/products";
import { InfluenceLabel } from "../ForecastAnalytics/InfluenceLabel";
import { ForecastSettingsMenu } from "../ForecastSettingsMenu";
import { ComponentGraph } from "../Graph/ComponentGraph";
import { ForecastSourceType } from "@domain/forecast/models/Forecast";
import { useProductUpdate } from "@features/products/hooks/useProductUpdate";

interface TimeTravelSeasonalityProps {
  sourceType: ForecastSourceType; // e.g. 'search'
  source: string;     // e.g. search term value
  connector: string;  // e.g. connector type
  canModifyParameters?: boolean;
}

// Simple week -> date helper for display only (approximate ISO week to date)
function weekYearToDateString(year: number, week: number): string {
  const date = new Date(year, 0, 1);
  const day = date.getDay();
  const diff = (week - 1) * 7 + (day <= 4 ? 1 - day : 8 - day);
  date.setDate(date.getDate() + diff);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

const TimeTravelSeasonality: React.FC<TimeTravelSeasonalityProps> = ({
  sourceType,
  source,
  connector,
  canModifyParameters = false,
}) => {
  const theme = useTheme();
  const { forecast, isLoading, error } = useForecast({ sourceType, source, connector });


  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { product } = useProduct(source);
  const { updateProduct } = useProductUpdate();

  const chartData = useMemo(() => {
    if (!forecast?.weeklyForecastComponents) return [];

    // Filter by the specified component type
    const components = forecast.weeklyForecastComponents
      .filter(comp => comp.type === WeeklyForecastComponentType.YEARLY);

    if (components.length === 0) return [];

    // Sort chronologically
    const sorted = components
      .slice()
      .sort((a, b) => (a.year === b.year ? a.week - b.week : a.year - b.year));

    // Calculate moving average
    const windowSize = 10; // e.g. 3-week moving average
    const movAvg = (arr: number[], idx: number, size: number) => {
      const start = Math.max(0, idx - Math.floor((size - 1) / 2));
      const end = Math.min(arr.length, start + size);
      const actualSize = end - start;
      const sum = arr.slice(start, end).reduce((acc, val) => acc + val, 0);
      return actualSize > 0 ? sum / actualSize : 0;
    };

    // Extract forecast quantities
    const forecastQuantities = sorted.map(w => w.forecastQuantity ?? 0);

    const currentYear = new Date().getFullYear();
    return sorted.map((w, i) => ({
      date: weekYearToDateString(w.year, w.week),
      year: w.year,
      week: w.week,
      forecast: movAvg(forecastQuantities, i, windowSize),
      forecastMin: movAvg(forecastQuantities, i, windowSize) - 1,
      forecastMax: movAvg(forecastQuantities, i, windowSize) + 1,
      forecastBands: [movAvg(forecastQuantities, i, windowSize) - 1, movAvg(forecastQuantities, i, windowSize) + 1],
    })).filter(w => w.year === currentYear);
  }, [forecast]);

  const mainSeason = useMemo(() => {
    if (!forecast?.weeklyForecastComponents) return null;

    // Filter by the specified component type
    const components = forecast.weeklyForecastComponents
      .filter(comp => comp.type === WeeklyForecastComponentType.YEARLY);

    if (components.length === 0) return null;

    // Define seasons by week ranges
    // Spring: weeks 9-22, Summer: weeks 23-35, Fall: weeks 36-48, Winter: weeks 49-52 and 1-8
    const getSeason = (week: number): 'Spring' | 'Summer' | 'Fall' | 'Winter' => {
      if (week >= 9 && week <= 22) return 'Spring';
      if (week >= 23 && week <= 35) return 'Summer';
      if (week >= 36 && week <= 48) return 'Fall';
      return 'Winter'; // weeks 49-52 and 1-8
    };

    // Group forecasts by season and calculate averages
    const seasonForecasts: Record<string, number[]> = {
      Spring: [],
      Summer: [],
      Fall: [],
      Winter: []
    };

    components.forEach(comp => {
      const season = getSeason(comp.week);
      const forecastValue = comp.forecastQuantity ?? 0;
      if (forecastValue > 0) {
        seasonForecasts[season].push(forecastValue);
      }
    });

    // Calculate average forecast per season
    const seasonAverages: Record<string, number> = {};
    Object.keys(seasonForecasts).forEach(season => {
      const values = seasonForecasts[season];
      seasonAverages[season] = values.length > 0
        ? values.reduce((sum, val) => sum + val, 0) / values.length
        : 0;
    });

    // Find the season with the highest average
    const sortedSeasons = Object.entries(seasonAverages)
      .sort(([, a], [, b]) => b - a);

    if (sortedSeasons.length === 0) return null;

    const [highestSeason, highestAvg] = sortedSeasons[0];
    const [, secondHighestAvg] = sortedSeasons[1] || [null, 0];

    // Calculate the difference threshold (15% of the highest average)
    const threshold = highestAvg * 0.15;
    const difference = highestAvg - secondHighestAvg;

    // If the difference is too low, return null (no main season)
    if (difference < threshold) {
      return null;
    }

    return highestSeason as 'Spring' | 'Summer' | 'Fall' | 'Winter' | null;
  }, [forecast]);


  const getSliderDefaultValue = () => {
    if (forecast?.parameters.seasonality_prior_scale) {
      return forecast.parameters.seasonality_prior_scale * 10;
    }

    return 5;
  };

  const handleSliderChange = (influence: number) => {
    updateProduct({
      productId: source,
      updateData: {
        forecastParameters: {
          ...product?.forecastParameters ?? {},
          seasonality_prior_scale: influence / 10
        }
      }
    });
  };

  const handleSelectModeChange = (mode: SeasonalityMode) => {
    updateProduct({
      productId: source,
      updateData: {
        forecastParameters: {
          ...product?.forecastParameters ?? {},
          seasonality_mode: mode as SeasonalityMode
        }
      }
    });
  };

  return (
    <Box sx={{
      position: 'relative',
      border: `1px solid ${APP_CONFIG.ui.theme.colors.border}`,
      borderRadius: 2,
      padding: 2,
      flex: 1,
    }}>
      <Stack direction="row" spacing={0.5} flex={1} justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2" color="text.secondary">
          Seasonality
        </Typography>

        {canModifyParameters && (
          <IconButton size="small"
            onClick={(event) => setAnchorEl(event.currentTarget)}
          ><MoreVert /></IconButton>
        )}

        <ForecastSettingsMenu
          title="Seasonality Settings"
          onModeChange={handleSelectModeChange} onInfluenceChange={handleSliderChange} defaultMode={forecast?.parameters.seasonality_mode} defaultInfluence={getSliderDefaultValue()} anchorEl={anchorEl} onClose={() => setAnchorEl(null)} />
      </Stack>

      <Typography variant="h6" color="text.secondary">{mainSeason}</Typography>

      <InfluenceLabel influence={forecast?.forecastAnalytics?.seasonal_influence} />


      {isLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" height={150}>
          <CircularProgress />
        </Box>
      )}

      <ComponentGraph chartData={chartData} />
    </Box>
  );
};

export default TimeTravelSeasonality;

